/**
 * CopyRoleModal Component
 *
 * Modal for copying an existing role with a new name and code.
 * Copies type, description, and permissions from the source role.
 * Does NOT copy assignments (users, groups, applications, serviceAccounts).
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { useCreateRole } from '../hooks/useCreateRole';
import { roleService } from '../../../api/role.service';
import { useAuth } from '../../../contexts/AuthContext';
import type { RoleSanitized, RoleCreateDTO } from '../../../types/role.types';

interface CopyRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  sourceRole: RoleSanitized;
}

interface CopyFormData {
  roleName: string;
  roleCode: string;
}

function generateCode(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 50);
}

export const CopyRoleModal: React.FC<CopyRoleModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  sourceRole,
}) => {
  const { tenant } = useAuth();
  const tenantId = tenant?.tenantId || '';

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [autoGenerateCode, setAutoGenerateCode] = useState<boolean>(true);
  const [isCopying, setIsCopying] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<CopyFormData>({
    defaultValues: {
      roleName: `Copy of ${sourceRole.roleName}`,
      roleCode: generateCode(`Copy of ${sourceRole.roleName}`),
    },
  });

  const roleName = watch('roleName');

  // Auto-generate code from name
  useEffect(() => {
    if (autoGenerateCode && roleName) {
      setValue('roleCode', generateCode(roleName));
    }
  }, [roleName, autoGenerateCode, setValue]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      const defaultName = `Copy of ${sourceRole.roleName}`;
      reset({
        roleName: defaultName,
        roleCode: generateCode(defaultName),
      });
      setAutoGenerateCode(true);
      setErrorMessage('');
      setSuccessMessage('');
    }
  }, [isOpen, sourceRole, reset]);

  const { mutate: createRole, isPending: isCreating } = useCreateRole(tenantId);

  const onSubmit = async (formData: CopyFormData) => {
    setErrorMessage('');
    setIsCopying(true);

    try {
      // Fetch full role details to ensure we have complete permissions
      const fullRole = await roleService.getRole(tenantId, sourceRole.roleId);

      const createData: RoleCreateDTO = {
        roleCode: formData.roleCode,
        roleName: formData.roleName,
        description: fullRole.description,
        path: fullRole.path || '/',
        type: fullRole.type,
        permissions: fullRole.permissions,
      };

      createRole(createData, {
        onSuccess: () => {
          setSuccessMessage('Role copied successfully!');
          setErrorMessage('');
          onSuccess();
          setTimeout(() => {
            reset();
            onClose();
            setSuccessMessage('');
          }, 1000);
        },
        onError: (error) => {
          setErrorMessage(error.message || 'Failed to copy role');
          setIsCopying(false);
        },
      });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to fetch role details');
      setIsCopying(false);
    }
  };

  const handleClose = () => {
    if (!isCreating && !isCopying) {
      setErrorMessage('');
      setSuccessMessage('');
      reset();
      onClose();
    }
  };

  const isPending = isCreating || isCopying;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Copy Role</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
            <p className="text-sm text-gray-600">
              Copying from: <span className="font-semibold">{sourceRole.roleName}</span>{' '}
              <span className="text-gray-400 font-mono">({sourceRole.roleCode})</span>
            </p>
          </div>

          <FormField label="Role Name" error={errors.roleName?.message} required>
            <Input
              {...register('roleName', {
                required: 'Role name is required',
                minLength: { value: 1, message: 'Role name is required' },
                maxLength: { value: 100, message: 'Role name must not exceed 100 characters' },
              })}
              placeholder="Enter role name"
              disabled={isPending}
            />
          </FormField>

          <FormField label="Role Code" error={errors.roleCode?.message} required>
            <div className="space-y-2">
              <Input
                {...register('roleCode', {
                  required: 'Role code is required',
                  minLength: { value: 3, message: 'Code must be at least 3 characters' },
                  maxLength: { value: 50, message: 'Code must not exceed 50 characters' },
                  pattern: {
                    value: /^[a-z0-9-]+$/,
                    message: 'Code must contain only lowercase letters, numbers, and hyphens',
                  },
                })}
                placeholder="enter-role-code"
                disabled={isPending || autoGenerateCode}
              />
              <label className="flex items-center gap-2 text-sm text-gray-500">
                <input
                  type="checkbox"
                  checked={autoGenerateCode}
                  onChange={(e) => setAutoGenerateCode(e.target.checked)}
                  disabled={isPending}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Auto-generate from name
              </label>
            </div>
          </FormField>

          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={isPending}>
              Copy Role
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
